import type { RouteNamedMap } from 'vue-router/auto-routes';
import { useAal } from '~/composables/useAal';
import { useCookieRedirect } from '~/composables/useCookieRedirect';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useSupabaseClient } from '~/composables/useSupabaseClient';
import { useSupabaseUser } from '~/composables/useSupabaseUser';

/**
 * Middleware to check if the user requires an AAL step-up. If so, redirects to the MFA page.
 */
export default defineNuxtRouteMiddleware(async (to) => {
	const user = useSupabaseUser();

	if (!user.value) {
		return;
	}

	// Skip middleware for routes where AAL check is not required (e.g., post-Google login confirmation).
	// Extend PageMeta interface if more routes need exclusion.
	const routesToIgnore: (keyof RouteNamedMap)[] = ['GlobalLogout', 'MerchantLogout', 'GlobalLoginConfirm', 'MerchantLoginConfirm', 'GlobalLogin', 'MerchantLogin'];

	if (routesToIgnore.includes(to.name)) {
		return;
	}

	const { isVerified, verifyAalStatus } = useAal();

	await verifyAalStatus();

	const needsMfaVerification = !isVerified.value;

	if (needsMfaVerification) {
		const merchantSlug = useMerchantSlug(to);
		const { data } = await useSupabaseClient().auth.getSession();

		// The challenge needs a session, so without one the MFA page is a dead end.
		if (!data.session) {
			const redirectInfo = useCookieRedirect();
			redirectInfo.path.value = to.path;

			return navigateTo(merchantSlug.value
				? { name: 'MerchantLogin', params: { merchantSlug: merchantSlug.value } }
				: { name: 'GlobalLogin' });
		}

		if (to.name === 'GlobalMfa' || to.name === 'MerchantMfa') {
			return;
		}

		return navigateTo(merchantSlug.value
			? {
				name: 'MerchantMfa',
				params: { merchantSlug: merchantSlug.value },
				query: { redirectTo: to.fullPath },
			}
			: {
				name: 'GlobalMfa',
				query: { redirectTo: to.fullPath },
			},
		);
	}

	// Redirect from MFA page if AAL is already sufficient.
	if (to.name === 'GlobalMfa' || to.name === 'MerchantMfa') {
		return navigateTo('/');
	}
});
